import React, { useState } from 'react';
import { Scene, DisciplineLayer } from '../types/models';

interface SceneEditorProps {
  scene: Scene;
  scenes: Scene[];
  userRole: string;
  editMode: boolean;
  onSceneChange: (sceneId: string) => void;
  onLayerSelect: (layerId: string) => void;
  onSceneUpdate: (scene: Scene) => void;
}

export const SceneEditor: React.FC<SceneEditorProps> = ({
  scene,
  scenes,
  userRole: _userRole,
  editMode,
  onSceneChange,
  onLayerSelect,
  onSceneUpdate: _onSceneUpdate,
}) => {
  const [activeLayerId, setActiveLayerId] = useState<string | null>(
    scene.layers[0]?.id || null
  );

  const handleLayerClick = (layerId: string) => {
    setActiveLayerId(layerId);
    onLayerSelect(layerId);
  };

  const currentSceneIndex = scenes.findIndex((s) => s.id === scene.id);
  const hasPrevious = currentSceneIndex > 0;
  const hasNext = currentSceneIndex < scenes.length - 1;

  const handlePrevious = () => {
    if (hasPrevious) {
      onSceneChange(scenes[currentSceneIndex - 1].id);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      onSceneChange(scenes[currentSceneIndex + 1].id);
    }
  };

  return (
    <div className="scene-editor">
      <header className="scene-header">
        <div className="scene-navigation">
          <button
            onClick={handlePrevious}
            disabled={!hasPrevious}
            aria-label="Previous scene"
            className="nav-btn"
          >
            ← Previous
          </button>
          <h2 className="scene-title">
            Scene {scene.sequenceOrder + 1}: {scene.title}
          </h2>
          <button
            onClick={handleNext}
            disabled={!hasNext}
            aria-label="Next scene"
            className="nav-btn"
          >
            Next →
          </button>
        </div>
        {scene.metadata.description && (
          <p className="scene-description">{scene.metadata.description}</p>
        )}
      </header>

      <div className="discipline-layers">
        <h3>Discipline Layers</h3>
        <div className="layer-list">
          {scene.layers.map((layer) => (
            <DisciplineLayerCard
              key={layer.id}
              layer={layer}
              isActive={layer.id === activeLayerId}
              onClick={() => handleLayerClick(layer.id)}
              editMode={editMode}
            />
          ))}
        </div>
      </div>

      <div className="cue-markers-section">
        <h3>Cue Markers</h3>
        {scene.cueMarkers.length === 0 ? (
          <p className="empty-state">No cue markers defined</p>
        ) : (
          <div className="cue-marker-list">
            {scene.cueMarkers.map((cue) => (
              <CueMarkerItem key={cue.id} cue={cue} layers={scene.layers} />
            ))}
          </div>
        )}
      </div>

      <div className="scene-metadata">
        <div className="metadata-item">
          <span className="label">Duration:</span>
          <span className="value">
            {scene.duration ? formatDuration(scene.duration) : 'Not set'}
          </span>
        </div>
        <div className="metadata-item">
          <span className="label">Cue Markers:</span>
          <span className="value">{scene.cueMarkers.length}</span>
        </div>
      </div>
    </div>
  );
};

interface DisciplineLayerCardProps {
  layer: DisciplineLayer;
  isActive: boolean;
  onClick: () => void;
  editMode: boolean;
}

const DisciplineLayerCard: React.FC<DisciplineLayerCardProps> = ({
  layer,
  isActive,
  onClick,
  editMode: _editMode,
}) => {
  const getStatusColor = (status: DisciplineLayer['status']) => {
    switch (status) {
      case 'empty':
        return 'status-empty';
      case 'draft':
        return 'status-draft';
      case 'complete':
        return 'status-complete';
      case 'approved':
        return 'status-approved';
      default:
        return '';
    }
  };

  const getStatusLabel = (status: DisciplineLayer['status']) => {
    switch (status) {
      case 'empty':
        return 'Empty';
      case 'draft':
        return 'Draft';
      case 'complete':
        return 'Complete';
      case 'approved':
        return 'Approved';
      default:
        return status;
    }
  };

  const getDisciplineIcon = (type: DisciplineLayer['disciplineType']) => {
    switch (type) {
      case 'music':
        return '♪';
      case 'dance':
        return '💃';
      case 'drama':
        return '🎭';
      default:
        return '';
    }
  };

  return (
    <div
      className={`discipline-layer-card ${isActive ? 'active' : ''} ${
        getStatusColor(layer.status)
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick();
        }
      }}
    >
      <div className="layer-header">
        <span className="discipline-icon">{getDisciplineIcon(layer.disciplineType)}</span>
        <h4 className="discipline-name">
          {layer.disciplineType.charAt(0).toUpperCase() + layer.disciplineType.slice(1)}
        </h4>
      </div>
      <div className="layer-status">
        <span className={`status-badge ${getStatusColor(layer.status)}`}>
          {getStatusLabel(layer.status)}
        </span>
      </div>
      {layer.metadata.completionPercentage !== undefined && (
        <div className="completion-bar">
          <div
            className="completion-fill"
            style={{ width: `${layer.metadata.completionPercentage}%` }}
          />
          <span className="completion-text">{layer.metadata.completionPercentage}%</span>
        </div>
      )}
      {layer.assignedRoles.length > 0 && (
        <div className="assigned-roles">
          <span className="roles-label">Roles:</span>
          <span className="roles-count">{layer.assignedRoles.length}</span>
        </div>
      )}
    </div>
  );
};

interface CueMarkerItemProps {
  cue: Scene['cueMarkers'][0];
  layers: DisciplineLayer[];
}

const CueMarkerItem: React.FC<CueMarkerItemProps> = ({ cue, layers }) => {
  const originLayer = layers.find((l) => l.id === cue.originLayerId);

  return (
    <div className="cue-marker-item">
      <div className="cue-header">
        <span className="cue-name">{cue.name}</span>
        <span className="cue-time">{formatDuration(cue.timePosition)}</span>
      </div>
      {cue.description && <p className="cue-description">{cue.description}</p>}
      <div className="cue-meta">
        <span className="origin-layer">
          Origin: {originLayer?.disciplineType || 'Unknown'}
        </span>
        {cue.linkedCues.length > 0 && (
          <span className="linked-count">{cue.linkedCues.length} linked</span>
        )}
      </div>
    </div>
  );
};

function formatDuration(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
